import React, { useState, useRef, useCallback } from "react"
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Menu,
    MenuItem,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import { useTranslation } from "react-i18next"
import * as exportJobService from "../../services/exportJobService"
import { SharedFilterValues } from "./SharedFilters"

interface ExportButtonProps {
    companyId: string
    reportType: string
    filters: SharedFilterValues
    extraFilters?: Record<string, string | undefined>
}

const POLL_INTERVAL_MS = 4000
const MAX_POLLS = 60

const STATUS_CHIP: Record<
    string,
    {
        labelKey: string
        color: "default" | "info" | "warning" | "success" | "error"
    }
> = {
    queued: {
        labelKey: "exportsComponents.exportButton.status.queued",
        color: "default",
    },
    processing: {
        labelKey: "exportsComponents.exportButton.status.processing",
        color: "info",
    },
    completed: {
        labelKey: "exportsComponents.exportButton.status.completed",
        color: "success",
    },
    failed: {
        labelKey: "exportsComponents.exportButton.status.failed",
        color: "error",
    },
}

const ExportButton: React.FC<ExportButtonProps> = ({
    companyId,
    reportType,
    filters,
    extraFilters,
}) => {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [emailDelivery, setEmailDelivery] = useState(false)
    const [userEmail, setUserEmail] = useState("")
    const [jobStatus, setJobStatus] = useState<string | null>(null)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [toastOpen, setToastOpen] = useState(false)
    const [successToastOpen, setSuccessToastOpen] = useState(false)
    const [successMsg, setSuccessMsg] = useState("")
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
    }, [])

    const startExport = async (format: "csv" | "pdf") => {
        setAnchorEl(null)
        setJobStatus("queued")
        setErrorMsg(null)
        stopPolling()

        try {
            const job = await exportJobService.createExportJob(companyId, {
                reportType,
                format,
                filters: {
                    outletId: filters.outletId || undefined,
                    registerId: filters.registerId || undefined,
                    dateFrom: filters.dateFrom || undefined,
                    dateTo: filters.dateTo || undefined,
                    ...extraFilters,
                },
                emailDelivery,
                userEmail: emailDelivery ? userEmail : undefined,
            })

            if (emailDelivery && userEmail) {
                setSuccessMsg(
                    String(
                        t(
                            "exportsComponents.exportButton.success.sentByEmail",
                            {
                                email: userEmail,
                            }
                        )
                    )
                )
                setSuccessToastOpen(true)
            } else {
                setSuccessMsg(
                    String(
                        t("exportsComponents.exportButton.success.queued", {
                            format: format.toUpperCase(),
                        })
                    )
                )
                setSuccessToastOpen(true)
            }

            let pollCount = 0
            pollRef.current = setInterval(async () => {
                pollCount++
                if (pollCount > MAX_POLLS) {
                    stopPolling()
                    setJobStatus("failed")
                    setErrorMsg(
                        t("exportsComponents.exportButton.errors.timedOut")
                    )
                    setToastOpen(true)
                    return
                }
                try {
                    const updated = await exportJobService.pollJobStatus(
                        companyId,
                        job.id
                    )
                    setJobStatus(updated.status)

                    if (updated.status === "completed") {
                        stopPolling()
                        exportJobService.downloadExportFile(companyId, job.id)
                    } else if (updated.status === "failed") {
                        stopPolling()
                        setErrorMsg(
                            updated.error_message ||
                                t(
                                    "exportsComponents.exportButton.errors.failed"
                                )
                        )
                        setToastOpen(true)
                    }
                } catch {
                    stopPolling()
                    setJobStatus("failed")
                    setErrorMsg(
                        t("exportsComponents.exportButton.errors.statusCheck")
                    )
                    setToastOpen(true)
                }
            }, POLL_INTERVAL_MS)
        } catch (err: any) {
            setJobStatus("failed")
            setErrorMsg(
                err.message ||
                    t("exportsComponents.exportButton.errors.startFailed")
            )
            setToastOpen(true)
        }
    }

    const chipInfo = jobStatus ? STATUS_CHIP[jobStatus] : null

    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={jobStatus === "queued" || jobStatus === "processing"}
            >
                {t("exportsComponents.exportButton.label")}
            </Button>

            {chipInfo && (
                <Chip
                    label={t(chipInfo.labelKey)}
                    color={chipInfo.color}
                    size="small"
                    variant="outlined"
                />
            )}
            <Snackbar
                open={toastOpen}
                autoHideDuration={6000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setToastOpen(false)}
                    severity="error"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {errorMsg}
                </Alert>
            </Snackbar>
            <Snackbar
                open={successToastOpen}
                autoHideDuration={5000}
                onClose={() => setSuccessToastOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSuccessToastOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {successMsg}
                </Alert>
            </Snackbar>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
                    <MenuItem onClick={() => startExport("csv")}>
                        {t("exportsComponents.exportButton.exportCsv")}
                    </MenuItem>
                    <MenuItem onClick={() => startExport("pdf")}>
                        {t("exportsComponents.exportButton.exportPdf")}
                    </MenuItem>
                    <Box sx={{ px: 1, pt: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={emailDelivery}
                                    onChange={(e) =>
                                        setEmailDelivery(e.target.checked)
                                    }
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {String(
                                        t(
                                            "exportsComponents.exportButton.sendByEmail"
                                        )
                                    )}
                                </Typography>
                            }
                        />
                        {emailDelivery && (
                            <TextField
                                size="small"
                                fullWidth
                                placeholder={String(
                                    t(
                                        "exportsComponents.exportButton.emailPlaceholder"
                                    )
                                )}
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                sx={{ mt: 0.5 }}
                            />
                        )}
                    </Box>
                </Box>
            </Menu>
        </Box>
    )
}

export default ExportButton
